import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import ArrowRight from 'assets/svgs/arrowRight24.svg';
import { Link } from 'react-router-dom';
import Parser from 'html-react-parser';
import { TcEventManualTiming } from 'features/tc-event-dashboard';
import { ROOM_STATUS } from 'shared/constants';
import {
  TaskCard,
  OpenRoom,
  TaskContent,
  CloseRoomSection,
  RoomSetup,
} from 'features/task-management';
import { AlertNotice, SimpleModal, TestDayWarningModal, AlertMessage } from 'shared/components';
import { isRoomOnline } from 'shared/helpers';
import CheckedCircleBold from 'assets/svgs/checkedCircleBold.svg';
import CloseCircleIcon from 'assets/svgs/closeCircle.svg';

import './RoomTasks.scss';

const setFirstTask = (task, idx, completedTasks) => {
  task.firstTask = idx === 0 && !get(completedTasks, 'length', 0);
  return task;
};

class RoomTasks extends Component {
  constructor() {
    super();

    this.state = {
      warningModalOpen: false,
      reviewRoomModalOpen: false,
      closeRoomModalOpen: false,
      closeRoomTimerNoticeOpen: false,
      showOpenRoomSuccessMessage: false,
      showSuccessMessage: false,
    };

    this.toggleReviewRoomModal = this.toggleReviewRoomModal.bind(this);
    this.closeWarningModal = this.closeWarningModal.bind(this);
    this.toggleCloseRoom = this.toggleCloseRoom.bind(this);
    this.closeTimerNoticeModal = this.closeTimerNoticeModal.bind(this);
    this.isMountedComponent = false;
  }

  componentDidMount() {
    this.isMountedComponent = true;
  }

  componentWillUnmount() {
    this.isMountedComponent = false;
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.room.status !== this.props.room.status &&
      this.props.room.status === ROOM_STATUS.opened
    ) {
      this.setState({ showOpenRoomSuccessMessage: true });
    }
    if (
      prevProps.upcomingTasks.length !== this.props.upcomingTasks.length &&
      !this.props.upcomingTasks.length
    ) {
      this.setState({ showSuccessMessage: true });
    }
    if (sessionStorage.getItem('onlineTestingRedirect')) {
      this.props.history.push(
        `/test-event/${this.props.tcEvent.id}/room/${this.props.room.id}/online-testing`
      );
    }
  }

  toggleReviewRoomModal() {
    const { isTestDay } = this.props;
    const { warningModalOpen, reviewRoomModalOpen } = this.state;

    if (!isTestDay && !warningModalOpen && !reviewRoomModalOpen) {
      this.setState({
        warningModalOpen: true,
      });
      return;
    }

    this.setState((prevState) => ({
      warningModalOpen: false,
      reviewRoomModalOpen: !prevState.reviewRoomModalOpen,
    }));
  }

  closeWarningModal() {
    this.setState({
      warningModalOpen: false,
    });
  }

  toggleCloseRoom() {
    if (this.props.roomStatus.activeRoomTimerStartDateTime) {
      this.setState({
        closeRoomTimerNoticeOpen: true,
      });
      return;
    }
    this.setState((prevState) => ({
      closeRoomModalOpen: !prevState.closeRoomModalOpen,
    }));
  }

  closeTimerNoticeModal() {
    this.setState({
      closeRoomTimerNoticeOpen: false,
    });
  }

  goToCloseRoom = () => {
    const { closeRoomModalOpen } = this.state;

    if (closeRoomModalOpen) {
      this.toggleCloseRoom();
    }
    this.props.goToCloseRoomPage();
  };

  removeOpenRoomSuccessMessage = () => {
    this.setState({
      showOpenRoomSuccessMessage: false,
    });
  };
  removeSuccessMessage = () => {
    this.setState({
      showSuccessMessage: false,
    });
  };

  render() {
    const {
      roomAvailableTasks,
      roomStatus,
      completedTasks,
      upcomingTasks,
      eventId,
      roomId,
      room,
      locations,
      loadingCompleteTask,
      tcEvent,
      currentTask,
      roomType,
    } = this.props;
    const { warningModalOpen, reviewRoomModalOpen, closeRoomModalOpen, closeRoomTimerNoticeOpen } =
      this.state;

    if (!roomStatus || isEmpty(roomStatus)) {
      return null;
    }
    const isPaused = roomStatus.currentPauseId && !roomStatus.currentUnpauseStartDateTime;
    const isInactive = roomStatus.status === ROOM_STATUS.inactive;
    const isClosed = roomStatus.status === ROOM_STATUS.closed;
    const isOpened = roomStatus.status === ROOM_STATUS.opened;

    return (
      <div className="room-tasks-container">
        {/*Close room button*/}
        {((completedTasks.length === roomAvailableTasks.length && isOpened) || isClosed) && (
          <CloseRoomSection
            goToCloseRoomPage={this.goToCloseRoom}
            isClosed={isClosed}
            isOnline={isRoomOnline(room.roomType)}
            isSpecialTesting={tcEvent.specialTestingFlag}
          />
        )}
        {/*Open room and tasks section*/}
        <div className="mt-24">
          {isInactive && (
            <RoomSetup
              openRoomButton={
                <OpenRoom
                  roomGroupedMaterials={this.props.roomGroupedMaterials}
                  roomTypes={this.props.roomTypes}
                  warningModalOpen={reviewRoomModalOpen}
                  toggleReviewRoomModal={this.toggleReviewRoomModal}
                  closeModal={this.toggleReviewRoomModal}
                  isInactive={roomType}
                  currentTask={currentTask}
                  history={this.props.history}
                  tcEvent={this.props.tcEvent}
                />
              }
              firstTask={currentTask}
              roomType={roomType}
            />
          )}
          {!isInactive && (
            <h5>
              {this.props.t('taskManagement.tasksTitle')} -
              <span className="fw-normal ps-1">
                {this.props.t('taskManagement.completedLabel', {
                  number: completedTasks.length,
                  total: roomAvailableTasks.length,
                })}
              </span>
            </h5>
          )}
          {!isInactive &&
            !(
              isClosed &&
              isRoomOnline(room.roomType) &&
              completedTasks.length === roomAvailableTasks.length
            ) && (
              <section className={`room-task-section ${isClosed ? 'closed-room' : ''}`}>
                {isClosed && completedTasks.length !== roomAvailableTasks.length ? (
                  <AlertNotice
                    message={this.props.t('taskManagement.closeRoom.infoMessageClosedAdvance')}
                  />
                ) : !upcomingTasks.length ? (
                  !isClosed && (
                    <div className="completion-message">
                      <CheckedCircleBold />
                      <span>{Parser(this.props.t('taskManagement.successCompletionTasks'))}</span>
                    </div>
                  )
                ) : (
                  <div>
                    <p className="mb-2">{this.props.t('taskManagement.tasksText')}</p>
                    {upcomingTasks.slice(0, 2).map((task, idx) => (
                      <div key={`task-${task.id}`}>
                        <TaskCard
                          active={isPaused ? false : idx < 1}
                          task={setFirstTask(task, idx, completedTasks)}
                          renderTask={this.props.renderTask}
                          details={
                            idx < 1
                              ? this.props.t('taskManagement.currentTask')
                              : this.props.t('taskManagement.nextTask')
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!isClosed && !upcomingTasks.length && (
                  <Link
                    to={`/test-event/${eventId}/room/${roomId}/tasks`}
                    href={`/test-event/${eventId}/room/${roomId}/tasks`}
                    className="btn-link icon-link">
                    <ArrowRight />
                    {this.props.t('taskManagement.viewTasksLabel')}
                  </Link>
                )}

                <TcEventManualTiming locations={locations} room={room} roomStatus={roomStatus} />

                {completedTasks.length < roomAvailableTasks.length && !isClosed && (
                  <div className="mt-1">
                    <h5>{this.props.t('taskManagement.closeRoom.titleCloseOnline')}</h5>
                    <div className="close-room-info-box">
                      <AlertNotice
                        message={this.props.t('taskManagement.closeRoom.closeRoomInfoOnline')}
                        type="warning"
                      />
                    </div>
                    <button
                      className="btn btn-small btn-remove"
                      onClick={this.toggleCloseRoom}
                      type="button">
                      <CloseCircleIcon className="close-room-icon" />
                      {this.props.t('taskManagement.closeRoom.titleCloseOnline')}
                    </button>
                  </div>
                )}
              </section>
            )}
        </div>

        {this.props.currentTask && (
          <TaskContent
            task={this.props.currentTask}
            loadingCompleteTask={loadingCompleteTask}
            modalOpen={this.props.taskCardOpen}
            toggleModal={this.props.toggleContentModal}
            renderTask={this.props.renderTask}
            userId={this.props.userId}
            submitTask={this.props.onSubmitTask}
            removeCompleted={this.props.removeCompleted}
            alreadyCompletedMessage={this.props.alreadyCompletedMessage}
          />
        )}
        <TestDayWarningModal
          warningModalOpen={warningModalOpen}
          onSubmit={this.toggleReviewRoomModal}
          closeWarningModal={this.closeWarningModal}
          message="taskManagement.openRoomWarning"
          startLabel="taskManagement.openRoom"
        />

        <SimpleModal
          isOpen={closeRoomModalOpen}
          icon="error_outline"
          onCancel={this.toggleCloseRoom}
          onConfirm={this.goToCloseRoom}
          message={this.props.t('taskManagement.closeRoom.modalMessage')}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('taskManagement.closeRoom.modalTitle')}
        />
        <SimpleModal
          containerClassName="close-room-timer-notice-modal"
          isOpen={closeRoomTimerNoticeOpen}
          onConfirm={this.closeTimerNoticeModal}
          message={this.props.t('taskManagement.closeRoom.timerModalMessage')}
          confirmLabel={this.props.t('common.labels.back')}
          headline={this.props.t('taskManagement.closeRoom.timerModalHeader')}
        />
        <AlertMessage
          show={this.state.showOpenRoomSuccessMessage}
          removeMessage={this.removeOpenRoomSuccessMessage}
          message={this.props.t('roomDashboard.messages.openRoom')}
        />
        <AlertMessage
          show={this.state.showSuccessMessage}
          removeMessage={this.removeSuccessMessage}
          message={this.props.t('roomDashboard.messages.completeAllTasks')}
        />
      </div>
    );
  }
}

export default withTranslation()(RoomTasks);
RoomTasks.propTypes = {
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  completedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  upcomingTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roomTypes: PropTypes.arrayOf(PropTypes.shape({})),
  roomStatus: PropTypes.shape({}).isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userId: PropTypes.number.isRequired,
  currentTask: PropTypes.shape({}),
  taskCardOpen: PropTypes.bool.isRequired,
  isTestDay: PropTypes.bool,
  toggleContentModal: PropTypes.func.isRequired,
  onSubmitTask: PropTypes.func.isRequired,
  removeCompleted: PropTypes.func.isRequired,
  renderTask: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

RoomTasks.defaultProps = {
  roomTypes: [],
  currentTask: {},
  isTestDay: false,
};
